import { useAuth } from "../../context/AuthContext";
import {
  buildYearSettings,
  getSchoolYearLabel,
  useSchoolYear,
} from "../../context/SchoolYearContext";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  useExpenses,
  usePlannedExpenses,
  useCollections,
  useTeacherNotices,
  useClassSettings,
} from "../../hooks/useData";

import {
  calcBalance,
  calcRealizedExpenses,
  calcPlannedExpenses,
  calcToReturn,
  calculateAvailableAfterPlanned,
  formatPLN,
  formatDate,
  getExpenseTotal,
  statusColor,
} from "../../lib/finance";
import {
  ACTIVE_COLLECTION_SECTION_SUBTITLE,
  formatCollectionDate,
  getActiveCollectionDescription,
  getCollectionPresentation,
} from "../../lib/collections/presentation";

const HERO_ILLUSTRATION_SRC = process.env.PUBLIC_URL + "/hero-illustration.png";

// ─── Ikony SVG ─────────────────────────────────────────────────────────────
function IconBalance() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  );
}

function IconAvailable() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function IconExpenses() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}

function IconPlanned() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function IconReturn() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
    </svg>
  );
}

// ─── StatCard ───────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  sub,
  extra,
  delay = 0,
  icon: Icon,
  lineClass,
  iconClass,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }}
      className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/80 p-5 shadow-[0_14px_40px_rgba(15,23,42,0.06)] backdrop-blur-sm"
    >
      {/* Subtle top accent line */}
      <div className={`absolute left-5 right-5 top-0 h-[2px] rounded-full ${lineClass}`} />

      <div className="flex items-start justify-between gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded-xl ${iconClass} text-white shadow-sm`}>
          {Icon && <Icon />}
        </div>
      </div>

      <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.85rem]">
        {value}
      </div>
      {sub && <div className="mt-2 text-xs leading-5 text-slate-400">{sub}</div>}
      {extra && <div className="mt-3">{extra}</div>}
    </motion.div>
  );
}

// ─── SectionCard ────────────────────────────────────────────────────────────
function SectionCard({ title, subtitle, action, children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }}
      className={`rounded-[30px] border border-white/70 bg-white/80 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm ${className}`}
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-xl font-semibold tracking-tight text-slate-900">{title}</div>
          {subtitle && <div className="mt-1 text-sm text-slate-500">{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </motion.div>
  );
}

// ─── Główny komponent ────────────────────────────────────────────────────────
export default function DashboardScreen() {
  const { isAdmin } = useAuth();
  const { selectedSchoolYear } = useSchoolYear();
  const { data: expenses = [] } = useExpenses();
  const { data: planned = [] } = usePlannedExpenses();
  const { data: collections = [] } = useCollections();
  const { data: notices = [] } = useTeacherNotices();
  const { data: settings } = useClassSettings();

  const yearSettings = buildYearSettings(settings, selectedSchoolYear);
  const schoolYearLabel = getSchoolYearLabel(selectedSchoolYear);

  const balance = calcBalance(expenses, yearSettings, collections);
  const realized = calcRealizedExpenses(expenses);
  const plannedTotal = calcPlannedExpenses(planned);
  const toReturn = calcToReturn(expenses);
  const availableAfterPlanned = calculateAvailableAfterPlanned({
    currentBalance: balance,
    reimbursementAmount: toReturn,
    plannedExpensesTotal: plannedTotal,
  });

  const activeCollection = collections.find(
    (c) => c.status === "active" || c.status === "aktywna"
  );
  const activePresentation = getCollectionPresentation(activeCollection);
  const activeCollectionIsFlexible = activePresentation.isFlexibleRange;
  const activeCollectionHasTarget = activePresentation.hasOverallTarget;
  const recentExpenses = expenses.slice(0, 5);
  const pendingNotices = notices.filter(
    (n) => n.status !== "zapłacone" && n.status !== "paid"
  );

  const collectionProgress = activePresentation.progress ?? 0;
  const collectionMissing = activePresentation.missing ?? 0;

  return (
    <div className="space-y-6 lg:space-y-8">

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative overflow-hidden rounded-[34px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.85)_0%,rgba(236,253,245,0.78)_35%,rgba(255,247,237,0.80)_100%)] px-6 py-7 shadow-[0_20px_60px_rgba(15,23,42,0.07)] backdrop-blur-sm sm:px-8 sm:py-9"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute right-[-20px] top-[-10px] h-36 w-36 rounded-full bg-amber-200/35 blur-2xl" />
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
              Panel główny
            </div>

            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-[2.6rem] lg:leading-tight">
              Skarbnik klasy{" "}
              <span
                style={{
                  background: "linear-gradient(135deg,#10b981 0%,#0d9488 50%,#e7c86d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {settings?.class_name ?? "Klasa"}
              </span>
            </h1>

            <p className="mt-3 max-w-xl text-base leading-7 text-slate-500 sm:text-lg">
              Przejrzysty podgląd zbiórek, wydatków i planowanych kosztów w jednym miejscu.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/collection"
                className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.28)] hover:-translate-y-0.5 transition"
              >
                Zobacz zbiórki
              </Link>

              <Link
                to="/expenses"
                className="inline-flex items-center justify-center rounded-2xl border border-white/80 bg-white/75 px-5 py-3 text-sm font-semibold text-slate-700 shadow hover:-translate-y-0.5 transition"
              >
                Przejdź do wydatków
              </Link>
            </div>
          </div>

          <div className="hidden lg:flex justify-center">
            <img
              src={HERO_ILLUSTRATION_SRC}
              alt="Skarbonka klasowa z monetami"
              className="w-full max-w-md object-contain drop-shadow-xl"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        </div>
      </motion.div>

      {/* ── STATS ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Saldo bieżące"
          value={formatPLN(balance)}
          sub="Aktualny stan konta"
          delay={0.1}
          icon={IconBalance}
          lineClass="bg-gradient-to-r from-emerald-400 to-emerald-500"
          iconClass="bg-gradient-to-br from-emerald-400 to-emerald-600"
        />
        <StatCard
          label="Dostępne środki"
          value={formatPLN(availableAfterPlanned)}
          sub="Po odjęciu zwrotów i planowanych wydatków"
          delay={0.14}
          icon={IconAvailable}
          lineClass={
            availableAfterPlanned < 0
              ? "bg-gradient-to-r from-amber-400 to-amber-500"
              : "bg-gradient-to-r from-teal-400 to-teal-500"
          }
          iconClass={
            availableAfterPlanned < 0
              ? "bg-gradient-to-br from-amber-400 to-amber-600"
              : "bg-gradient-to-br from-teal-400 to-teal-600"
          }
        />
        <StatCard
          label={`Wydatki zrealizowane${schoolYearLabel ? ` w roku szkolnym ${schoolYearLabel}` : ""}`}
          value={formatPLN(realized)}
          sub="Łączna wartość"
          delay={0.18}
          icon={IconExpenses}
          lineClass="bg-gradient-to-r from-slate-400 to-slate-500"
          iconClass="bg-gradient-to-br from-slate-400 to-slate-600"
        />
        <StatCard
          label="Wydatki planowane"
          value={formatPLN(plannedTotal)}
          sub="Zaplanowane koszty"
          delay={0.22}
          icon={IconPlanned}
          lineClass="bg-gradient-to-r from-amber-400 to-amber-500"
          iconClass="bg-gradient-to-br from-amber-400 to-amber-600"
        />
        <StatCard
          label="Do zwrotu"
          value={formatPLN(toReturn)}
          sub="Kwota do zwrotu skarbnikowi"
          delay={0.26}
          icon={IconReturn}
          lineClass="bg-gradient-to-r from-orange-400 to-orange-500"
          iconClass="bg-gradient-to-br from-orange-400 to-orange-600"
        />
      </div>

      <p className="px-1 text-xs leading-6 text-slate-400">
        Dostępne środki pokazują kwotę po odjęciu zwrotów dla skarbnika oraz
        wydatków planowanych. Saldo bieżące pokazuje aktualny stan konta.
      </p>

      {/* ── MAIN SECTIONS ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.35fr]">

        {/* Aktywna zbiórka */}
        <SectionCard
          title="Aktywna zbiórka"
          subtitle={ACTIVE_COLLECTION_SECTION_SUBTITLE}
          delay={0.3}
          action={
            <Link
              to="/collection"
              className="rounded-2xl border border-white/80 bg-white/75 px-4 py-2 text-xs font-semibold text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-emerald-50 hover:text-emerald-700"
            >
              Szczegóły
            </Link>
          }
        >
          {activeCollection ? (
            <div className="space-y-5">
              <div className="rounded-[24px] bg-gradient-to-br from-emerald-50 via-teal-50 to-amber-50 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">
                      {activeCollection.name}
                    </div>
                    <div className="mt-1 text-sm leading-6 text-slate-500">
                      {getActiveCollectionDescription(activeCollection)}
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-end gap-2">
                    {activePresentation.isVoluntary && (
                      <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 shadow-sm">
                        Zbiórka dobrowolna
                      </span>
                    )}
                    {!activePresentation.affectsClassBalance && (
                      <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 shadow-sm">
                        Poza saldem klasy
                      </span>
                    )}
                    <span className="rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm">
                      aktywna
                    </span>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="text-slate-500">Zebrano</span>
                    <span className="font-medium text-slate-800">
                      {formatPLN(activeCollection.current_amount)}
                      {activeCollectionHasTarget
                        ? ` / ${formatPLN(activeCollection.target_amount)}`
                        : ""}
                    </span>
                  </div>

                  {activeCollectionIsFlexible && (
                    <div className="mb-3 text-sm font-medium text-emerald-800">
                      {activePresentation.contributionLabel}
                    </div>
                  )}

                  {activeCollectionHasTarget ? (
                    <>
                      <div className="h-2.5 overflow-hidden rounded-full bg-white/80">
                        <motion.div
                          className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-amber-300"
                          initial={{ width: 0 }}
                          animate={{ width: `${collectionProgress}%` }}
                          transition={{ delay: 0.45, duration: 0.9, ease: "easeOut" }}
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                        <span className="font-medium text-slate-700">{collectionProgress.toFixed(0)}%</span>
                        <span>Brakuje: {formatPLN(collectionMissing)}</span>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    {activeCollectionHasTarget ? "Cel" : activeCollectionIsFlexible ? "Wpłata dobrowolna" : "Cel"}
                  </div>
                  <div className="mt-2 text-lg font-semibold text-slate-900">
                    {activeCollectionHasTarget
                      ? formatPLN(activeCollection.target_amount)
                      : activeCollectionIsFlexible
                        ? `${formatPLN(activePresentation.minContribution)}–${formatPLN(activePresentation.maxContribution)}`
                        : "—"}
                  </div>
                </div>

                <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Zebrano
                  </div>
                  <div className="mt-2 text-lg font-semibold text-slate-900">
                    {formatPLN(activeCollection.current_amount)}
                  </div>
                </div>
              </div>

              {(activeCollection.start_date || activeCollection.end_date) && (
                <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                  {activeCollection.start_date && (
                    <span>Wpłaty od: <strong>{formatCollectionDate(activeCollection.start_date)}</strong></span>
                  )}
                  {activeCollection.end_date && (
                    <span>Wpłaty do: <strong>{formatCollectionDate(activeCollection.end_date)}</strong></span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center text-sm text-slate-500">
              Brak aktywnej zbiórki
            </div>
          )}
        </SectionCard>

        {/* Ostatnie wydatki */}
        <SectionCard
          title="Ostatnie wydatki"
          subtitle="Najnowsze wpisy dodane do rozliczeń"
          delay={0.34}
          action={
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Link
                  to="/admin"
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-300 px-4 py-2 text-xs font-semibold text-slate-900 shadow-[0_10px_20px_rgba(251,191,36,0.25)] transition-transform duration-200 hover:-translate-y-0.5"
                >
                  + Dodaj
                </Link>
              )}
              <Link
                to="/expenses"
                className="rounded-2xl border border-white/80 bg-white/75 px-4 py-2 text-xs font-semibold text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-emerald-50 hover:text-emerald-700"
              >
                Wszystkie
              </Link>
            </div>
          }
        >
          <div className="space-y-2.5">
            {recentExpenses.length === 0 && (
              <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center text-sm text-slate-500">
                Brak wydatków
              </div>
            )}

            {recentExpenses.map((e) => (
              <div
                key={e.id}
                className="group flex flex-col gap-2.5 rounded-[22px] border border-slate-100 bg-slate-50/70 p-4 transition-colors hover:bg-white/80 md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900">
                    {e.occasion || "Bez okazji"}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-slate-500">
                    {e.item_or_service || "Brak opisu"} · {formatDate(e.purchase_date)}
                  </div>
                </div>

                <div className="flex items-center gap-3 self-start shrink-0 md:self-center">
                  <span className={`badge ${statusColor(e.status)}`}>{e.status}</span>
                  <span className="text-sm font-semibold text-slate-900">
                    {formatPLN(getExpenseTotal(e))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* ── NOTICES ───────────────────────────────────────────────────────── */}
      {pendingNotices.length > 0 && (
        <SectionCard
          title="Przypomnienia wychowawcy"
          subtitle="Najważniejsze pozycje oczekujące na opłacenie"
          delay={0.4}
          action={
            <Link
              to="/notices"
              className="rounded-2xl border border-white/80 bg-white/75 px-4 py-2 text-xs font-semibold text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-emerald-50 hover:text-emerald-700"
            >
              Wszystkie
            </Link>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {pendingNotices.slice(0, 3).map((n) => (
              <div
                key={n.id}
                className="rounded-[24px] border border-amber-100/60 bg-amber-50/40 p-5"
              >
                <div className="text-base font-semibold text-slate-900">{n.title}</div>
                <div className="mt-2 text-sm leading-6 text-slate-500">
                  {n.description}
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <span className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                    Termin: {formatDate(n.due_date)}
                  </span>

                  {n.amount_per_child && (
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {formatPLN(n.amount_per_child)}/dziecko
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
